import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { restManager } from 'app/ajs-upgraded-providers';
import { Subject } from 'rxjs';
import { ConfigurationManagerService, EntityManagerService, UserActiveSessionManagerService } from 'src/app/core';
import { WaitDialogService } from 'src/app/dialogs';
import { WaitDialogModel } from 'src/app/dialogs/wait-dialog/wait-dialog.component';
import { Items } from 'tilby-models';
import {
  NewMovementErrorCode,
  NewMovementFormGroup,
  NewMovementMode,
  NewMovementRowValue,
  PreparedMovement,
  STOCK_TRANSFER_CAUSE
} from '../models/stock-new-movement.model';


@Injectable()
export class StockNewMovementSaveService {

  private readonly restManagerService = inject(restManager);
  private readonly entityManager = inject(EntityManagerService);
  private readonly translateService = inject(TranslateService);
  private readonly waitDialogService = inject(WaitDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);

  
  createMovements(form: NewMovementFormGroup, mode: NewMovementMode | null): PreparedMovement[] {
    const header = form.getRawValue();
    const user = this.userActiveSessionManagerService.getSession();

    const cause = header.movementCause;

    if (!cause || typeof cause !== 'object') {
      throw 'MISSING_MOVEMENT_CAUSE' as NewMovementErrorCode;
    }

    if (!header.sourceLocationId) {
      throw 'MISSING_LOCATION_TARGET' as NewMovementErrorCode;
    }

    const targetLocationId = header.targetLocationId === header.sourceLocationId ? null : header.targetLocationId;
    let supplier = typeof header.supplier === 'object' ? header.supplier : null;
    let supplierOrder = header.supplierOrder;

    if (cause.value === STOCK_TRANSFER_CAUSE) {
      if (!targetLocationId) {
        throw 'MISSING_LOCATION_TARGET' as NewMovementErrorCode;
      }

      // il fornitore e l'ordine hanno senso solo nel ricevimento merce
      if (mode !== 'goods_delivery') {
        supplier = null;
        supplierOrder = null;
      }
    }

    return form.controls.rows.getRawValue().map((rowValue, rowIndex) => ({
      rowIndex,
      rowValue,
      payload: this.createMovement(rowValue, {
        cause,
        sourceLocationId: header.sourceLocationId!,
        targetLocationId,
        supplier,
        supplierOrder,
        notes: header.notes,
        date: header.date,
        user
      })
    }));
  }

  private createMovement(row: NewMovementRowValue, context: {
    cause: NonNullable<ReturnType<NewMovementFormGroup['getRawValue']>['movementCause']>;
    sourceLocationId: number;
    targetLocationId: number | null;
    supplier: any;
    supplierOrder: string | null;
    notes: string | null;
    date: string | null;
    user: any;
  }): Record<string, any> {
    const entry = row.entry;

    if (!entry || typeof entry !== 'object') {
      throw 'MISSING_ENTRY' as NewMovementErrorCode;
    }

    if (row.quantity == null) {
      throw 'MISSING_QUANTITY' as NewMovementErrorCode;
    }

    const { cause, user } = context;

    const movement: Record<string, any> = {
      price: row.price ?? undefined,
      perc_discount: row.perc_discount ?? undefined,
      quantity: row.quantity,
      type: cause.type,
      supplier_order: context.supplierOrder || undefined,
      notes: context.notes || undefined,
      entry_type: entry._entryType,
      date: new Date(context.date ?? new Date()).toISOString(),
      operator_id: user?.id,
      operator_username: user?.username,
      operator_fullname: (user?.first_name && user?.last_name) ? `${user.first_name} ${user.last_name}` : null,
      stock_location_id: context.sourceLocationId,
      supplier_name: context.supplier?.name,
      supplier_id: context.supplier?.id,
      target_location_id: cause.value === STOCK_TRANSFER_CAUSE ? context.targetLocationId : undefined
    };

    switch (entry._entryType) {
      case 'item':
        movement['item_id'] = entry.id;
        movement['combination_id'] = entry.combination_id;
        break;
      case 'raw_material':
        movement['raw_material_id'] = entry.id;
        break;
    }

    switch (movement['type']) {
      case 'load':
        movement['load_cause'] = cause.value;
        break;
      case 'unload':
        delete movement['supplier_id'];
        delete movement['supplier_name'];
        movement['unload_cause'] = cause.value;
        movement['quantity'] *= -1;
        break;
    }

    // una rettifica positiva con quantità negativa è in realtà uno scarico
    if (movement['type'] === 'load' && movement['load_cause'] === 'positive_adjustment' && movement['quantity'] < 0) {
      Object.assign(movement, {
        load_cause: undefined,
        type: 'unload',
        unload_cause: 'negative_adjustment'
      });
    }

    return movement;
  }

  /**
   * Invia i movimenti uno alla volta mostrando l'avanzamento.
   * @returns gli indici (rowIndex) dei movimenti inviati con successo
   */
  async send(movements: PreparedMovement[]): Promise<Set<number>> {
    const sent = new Set<number>();
    const stateSubject: WaitDialogModel.StatusSubject = new Subject();
    const progressMessage = () => this.translateService.instant('STOCK.NEW_MOVEMENT.SEND_MOVEMENTS', { first: sent.size, second: movements.length });

    const promise = (async () => {
      for (const movement of movements) {
        try {
          await this.restManagerService.post('stock_movements', movement.payload);
          sent.add(movement.rowIndex);
          stateSubject.next({ message: progressMessage() });
        } catch (err) {
          // il movimento resta nell'elenco da inviare
        }
      }
    })();

    await this.waitDialogService.openDialog({
      data: { message: progressMessage(), promise, stateSubject }
    });

    return sent;
  }

  // aggiorna il costo del prodotto con l'ultimo prezzo di acquisto inserito
  async updateItemsCost(sentMovements: PreparedMovement[], getOriginalItem: (id: number) => Items | undefined) {
    if (!this.configurationManagerService.isModuleEnabled('items') || this.configurationManagerService.getPreference('stock_movements.disable_item_cost_update' as any)) {
      return;
    }

    const priceByItem = new Map<number, number>();

    for (const { payload } of sentMovements) {
      if (payload['entry_type'] === 'item' && payload['price'] != null && !priceByItem.has(payload['item_id'])) {
        priceByItem.set(payload['item_id'], payload['price']);
      }
    }

    for (const [itemId, price] of priceByItem) {
      const item = getOriginalItem(itemId);

      if (item && item.cost !== price) {
        try {
          await this.entityManager.items.putOneOnline({ ...item, cost: price });
        } catch (err) {
          // l'aggiornamento del costo è secondario rispetto al movimento
        }
      }
    }
  }
}
